import type { SportCarrier, SportDetails } from '../api'
import { API_BASE, assetUrl } from '../api'

// Distribution widget — which sportsbooks carry the sport. Renders each
// carrier as a brand-colored logo tile (per the affiliate-logos design
// handoff). brand_color + logo_url come from the API (Supabase columns
// on sportsbooks). Carriers with neither field fall back to a neutral
// text tile so long-tail books (BetMoto, Betparx, etc.) still render
// before brand assets land.
//
// Logo paths from the API are relative ('/sportsbooks/<file>') — same-
// origin partner-portal use is fine as-is, but cross-origin embeds need
// the absolute URL, so the renderer prepends API_BASE.
//
// Sub-divisions (WoO Sprint Car / Late Model / Super Dirt Car Series, X
// Games Summer / Aspen / League) are intentionally collapsed here — the
// public-facing widget answers "which books carry this sport at all?"
// and a duplicate logo per division reads as visual clutter. Division
// granularity stays visible in the /distribution admin editor.

interface Props {
  sport: SportDetails | null
  loading?: boolean
  // Cap the grid at the first N carriers (API order is priority-ranked, so
  // this is "top N books"). Omit for all — mirrors the SDK's maxBooks.
  maxBooks?: number
}

function dedupeBySportsbook(carriers: SportCarrier[]): SportCarrier[] {
  const seen = new Set<number>()
  const out: SportCarrier[] = []
  for (const c of carriers) {
    if (seen.has(c.sportsbook_id))
      continue
    seen.add(c.sportsbook_id)
    out.push(c)
  }
  return out
}

export default function DistributionWidget({ sport, loading = false, maxBooks }: Props) {
  if (loading) {
    return <p className="b-mono-dim ad-list-empty">LOADING…</p>
  }
  if (!sport) {
    return <p className="b-mono-dim ad-list-empty">no distribution data</p>
  }

  const deduped = dedupeBySportsbook(sport.carriers)
  const carriers = maxBooks && maxBooks > 0 ? deduped.slice(0, maxBooks) : deduped
  const count = deduped.length

  return (
    <div className="ew-distribution">
      {count === 0
        ? <p className="b-mono-dim ad-list-empty">no books carry this sport yet</p>
        : (
            <div className="ew-logo-grid">
              {carriers.map((c) => {
                const name = c.sportsbook_name ?? `#${c.sportsbook_id}`
                const hasLogo = c.logo_url != null
                const hasBrand = c.brand_color != null
                // Logo present → image tile on brand color.
                // Brand color but no logo → text on brand color.
                // Neither → neutral fallback (paper bg).
                const tileClass = hasLogo
                  ? `ew-logo-tile${c.logo_fill ? ' is-fill' : ''}`
                  : hasBrand
                    ? 'ew-logo-tile is-brand-text'
                    : 'ew-logo-tile is-text'
                const style = hasBrand ? { background: c.brand_color! } : undefined
                const dataBook = name.toLowerCase().replace(/\s+/g, '-')
                const inner = (
                  <div className={tileClass} style={style} title={name} data-book={dataBook}>
                    {hasLogo
                      ? <img src={assetUrl(c.logo_url)} alt={name} loading="lazy" decoding="async" />
                      : <span className="ew-logo-tile-text">{name}</span>}
                  </div>
                )
                return (
                  <div key={c.sportsbook_id} className="ew-logo-tile-wrap">
                    {c.affiliate_url
                      ? (
                          <a
                            href={c.affiliate_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="ew-logo-tile-link"
                            aria-label={`Open ${name}`}
                          >
                            {inner}
                          </a>
                        )
                      : inner}
                  </div>
                )
              })}
            </div>
          )}
    </div>
  )
}
